import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';
import { cn } from './cn';

/** Lists retain their actual UL/LI boundaries; menu glyph/text slots remain
 * plain presentation and borrow the menu's one row/keyboard engine. */
export const List = forwardRef<
  HTMLUListElement,
  HTMLAttributes<HTMLUListElement>
>(function List({ className, ...props }, ref) {
  return (
    <ul
      {...props}
      ref={ref}
      className={cn(
        'm-0 list-none py-[calc(var(--minerva-spacing)*1)]',
        className
      )}
    />
  );
});
export const ListItem = forwardRef<
  HTMLLIElement,
  HTMLAttributes<HTMLLIElement>
>(function ListItem({ className, ...props }, ref) {
  return (
    <li
      {...props}
      ref={ref}
      className={cn('flex min-w-0 items-center gap-2 px-4 py-2', className)}
    />
  );
});
export const ListItemIcon = forwardRef<
  HTMLSpanElement,
  HTMLAttributes<HTMLSpanElement>
>(function ListItemIcon({ className, ...props }, ref) {
  return (
    <span
      {...props}
      ref={ref}
      data-slot="list-item-icon"
      className={cn(
        'inline-flex shrink-0 items-center justify-center text-inherit',
        className
      )}
    />
  );
});
export interface ListItemTextProps extends HTMLAttributes<HTMLDivElement> {
  primary?: ReactNode;
  secondary?: ReactNode;
}
export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  function ListItemText(
    { className, children, primary, secondary, ...props },
    ref
  ) {
    return (
      <div
        {...props}
        ref={ref}
        data-slot="list-item-text"
        className={cn('min-w-0 flex-1', className)}
      >
        <span
          data-slot="list-item-primary"
          className="block whitespace-normal text-sm"
        >
          {primary ?? children}
        </span>
        {secondary !== undefined ? (
          <span
            data-slot="list-item-secondary"
            className="mt-[calc(var(--minerva-spacing)*0.25)] block whitespace-normal text-muted-foreground text-xs"
          >
            {secondary}
          </span>
        ) : null}
      </div>
    );
  }
);
